/* Campsite directory — placeholder sites for the initial launch.
 * Each site carries its own safety block so the SafetyPanel on the
 * detail page can render location-specific mountain rescue, A&E and
 * signal information.  Placeholders (marked `placeholder: true`) are
 * flagged in the UI as "coming soon" rather than shown as fact. */

export const CAMPSITES = [
  {
    slug: 'glen-affric-basecamp',
    name: 'Glen Affric Basecamp',
    region: 'Highland · Glen Affric',
    tagline: 'Ancient Caledonian pinewood, still lochs, deep quiet.',
    image:
      'https://images.unsplash.com/photo-1687876790754-2a92a8f0686d?fm=jpg&q=70&w=1600&auto=format&fit=crop',
    description:
      'A small, low-impact basecamp set among Scots pine on the north shore of Loch Affric. Ideal jumping-off point for the Affric–Kintail Way and gentle loch-side spins on the e-bike.',
    features: [
      'Loch-side pitches',
      'Composting loos',
      'Firewood on request',
      'Waymarked trails',
    ],
    safety: {
      mountainRescue: {
        team: 'Kintail Mountain Rescue Team',
        instruction: 'Dial 999 · ask for Police, then Mountain Rescue',
        note: 'Cover Glen Affric, Kintail and the Five Sisters. Request "Kintail MRT" when connected.',
      },
      medical: {
        name: 'Raigmore Hospital A&E · Inverness',
        distance: 'Approx. 32 mi north-east',
        postcode: 'IV2 3UJ',
        placeholder: false,
      },
      phoneSignal:
        'Reliable 4G at the eastern car park by Dog Falls. Signal drops sharply past the loch head — send messages before you set off.',
      osGrid: 'NH 216 234',
      what3words: '///filled.count.soap',
    },
    accessibleParking: {
      name: 'Dog Falls Car Park',
      postcode: 'IV4 7ND',
      distanceToTrailhead: 'Approx. 350 m to the loch-side trailhead',
      surface: 'Compacted gravel with two tarmac bays nearest the gate',
      bayWidth: '2 designated accessible bays · extra-wide (approx. 3.6 m) for side transfer',
      heightBarrier: 'Height barrier at entry: 2.1 m · lifts on request via the Forestry &amp; Land Scotland number posted at the gate',
      notes: 'Blue Badge holders may park in the two marked bays nearest the gate. Toilets 40 m from the accessible bays; nearest picnic bench is level and firm underfoot.',
    },
  },
  {
    slug: 'cairngorms-wild-camp',
    name: 'Cairngorms Wild Camp',
    region: 'Cairngorms National Park · Rothiemurchus',
    tagline: 'High granite, ancient forest, and long summer light.',
    image:
      'https://images.unsplash.com/photo-1500534623283-312aade485b7?fm=jpg&q=70&w=1600&auto=format&fit=crop',
    description:
      'A dispersed wild-camping cluster within Rothiemurchus, run under the Scottish Outdoor Access Code. Bring everything in, take everything out — the estate loves you for it.',
    features: [
      'Dispersed wild pitches',
      'Bear-hang tree lines',
      'Bike-friendly forest tracks',
      'Ranger patrols',
    ],
    safety: {
      mountainRescue: {
        team: 'Cairngorm Mountain Rescue Team',
        instruction: 'Dial 999 · ask for Police, then Mountain Rescue',
        note: 'Cover the northern Cairngorms plateau. Request "Cairngorm MRT" when connected.',
      },
      medical: {
        name: 'Raigmore Hospital A&E · Inverness',
        distance: 'Approx. 34 mi north',
        postcode: 'IV2 3UJ',
        placeholder: false,
      },
      phoneSignal:
        'Signal at the Glenmore visitor centre and along the Old Logging Way. Drops out fully once you climb above the treeline — plan accordingly.',
      osGrid: 'NH 958 095',
      what3words: '///pines.gather.dawn',
    },
    accessibleParking: {
      name: 'Glenmore Visitor Centre Car Park',
      postcode: 'PH22 1QU',
      distanceToTrailhead: 'Approx. 120 m to the forest trail entrance',
      surface: 'Tarmac throughout · level approach from bay to path',
      bayWidth: '4 designated accessible bays · standard width (approx. 3.3 m) plus one extra-wide near the visitor centre',
      heightBarrier: 'No height barrier · pay-and-display (Blue Badge holders exempt)',
      notes: 'Fully step-free route from the accessible bays into the visitor centre café, toilets and the start of the Old Logging Way. Ranger station open daily for current path conditions.',
    },
  },
  {
    slug: 'loch-lomond-shores',
    name: 'Loch Lomond Shores',
    region: 'Loch Lomond & The Trossachs · West Shore',
    tagline: 'Broad water, gentle hills, and a proper Highland welcome.',
    image:
      'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?fm=jpg&q=70&w=1600&auto=format&fit=crop',
    description:
      'Family-friendly shoreside pitches along the western bank of Loch Lomond. Flat forest tracks, gentle e-bike loops, and one of the shortest signal-to-summit ratios in the park.',
    features: [
      'Loch-shore pitches',
      'Hard-standing available',
      'Family loops',
      'Rangers on site',
    ],
    safety: {
      mountainRescue: {
        team: 'Lomond Mountain Rescue Team',
        instruction: 'Dial 999 · ask for Police, then Mountain Rescue',
        note: 'Cover Loch Lomond and the southern Trossachs. Request "Lomond MRT" when connected.',
      },
      medical: {
        name: 'Royal Alexandra Hospital A&E · Paisley',
        distance: 'Approx. 22 mi south',
        postcode: 'PA2 9PN',
        placeholder: false,
      },
      phoneSignal:
        'Solid 4G across the west shore. Weakest around Rowardennan pier — call before you push north on the West Highland Way.',
      osGrid: 'NS 359 984',
      what3words: '///boots.harbour.oak',
    },
    accessibleParking: {
      name: 'Loch Lomond Shores Main Car Park',
      postcode: 'G83 8QL',
      distanceToTrailhead: 'Approx. 80 m to the shoreline path',
      surface: 'Tarmac · gently sloped ramp to the loch-side promenade',
      bayWidth: '12 designated accessible bays · mix of standard and extra-wide, several with drop-kerb loading zones',
      heightBarrier: 'No height barrier · pay-and-display machines have tactile keypads and audio prompts',
      notes: 'The gentlest introduction to Highland loch-side riding — flat, well-surfaced and fully step-free from car boot to shoreline. Accessible toilets and a Changing Places facility on site.',
    },
  },
];
